import { useCallback, useEffect, useState } from 'react';
import { api, getToken, clearToken } from './api.js';
import { Shell, canView } from './components/Shell.jsx';
import { ViewLink } from './components/ViewLink.jsx';
import { Auth } from './pages/Auth.jsx';
import { Overview, NewEntity } from './pages/Overview.jsx';
import { TrialBalance, IncomeStatement, BalanceSheet, GeneralLedger, VatReport, AgeAnalysis, CashBookListing, ReconciliationStatement } from './pages/Reports.jsx';
import { JournalEntry, Invoice, Payment, Contacts } from './pages/Capture.jsx';
import { People } from './pages/People.jsx';
import { EntitySettings, OpeningBalances, Periods } from './pages/Admin.jsx';
import { ChartOfAccounts } from './pages/ChartOfAccounts.jsx';
import { Documents } from './pages/Documents.jsx';
import { Slips } from './pages/Slips.jsx';
import { SlipReview } from './pages/SlipReview.jsx';
import { AllocationRules } from './pages/AllocationRules.jsx';
import { DescriptionRules } from './pages/DescriptionRules.jsx';
import { ReferencePrefixes } from './pages/ReferencePrefixes.jsx';
import { Account } from './pages/Account.jsx';
import { Code } from './pages/Code.jsx';
import { showCodeTab } from './lib/codeTab.js';
import { SetPassword, ForgotPassword } from './pages/SetPassword.jsx';
import { ApiKeys } from './pages/ApiKeys.jsx';
import { Items } from './pages/Items.jsx';
import { StockOnHand } from './pages/StockOnHand.jsx';
import { StockTake } from './pages/StockTake.jsx';
import { StockAdjustments } from './pages/StockAdjustments.jsx';
import { StockLocations } from './pages/StockLocations.jsx';
import { CashBook } from './pages/CashBook.jsx';
import { BankReconciliation } from './pages/BankReconciliation.jsx';
import { parseRoute, routeUrl, DEFAULT_VIEW } from './lib/routing.js';
import { readSetPasswordRoute } from './lib/setPasswordRoute.js';

/**
 * Invitation and reset links carry their token in the URL FRAGMENT, so it
 * never reaches the server in a request line and never lands in an access log
 * or a Referer header. Read it once at start-up, then strip it from the bar.
 *
 * IronKeep's /set-password links are the one exception: it hands out
 * `?token=…` because it has no browser of its own to put the token in a
 * fragment with. That means it DOES reach the server in a request line, so it
 * is stripped here — synchronously, before this function returns and before
 * any effect below gets a chance to run a request whose Referer could carry
 * it forward.
 */
function readTokenRoute() {
  const path = window.location.pathname;
  if (path === '/set-password') {
    const { search, hash } = window.location;
    const route = readSetPasswordRoute(search, hash);
    window.history.replaceState({}, '', path);
    return route; // { mode: 'invite' | 'reset', token } or { mode: null }
  }
  const mode = path === '/accept-invite' ? 'invite' : path === '/reset' ? 'reset' : null;
  if (!mode) return null;
  const token = window.location.hash.replace(/^#/, '');
  return token ? { mode, token } : null;
}

export function App() {
  const [tokenRoute, setTokenRoute] = useState(readTokenRoute);
  const [forgot, setForgot] = useState(false);
  const [session, setSession] = useState(null);
  // Boot from the address bar, so a deep link, a refresh and a restored tab all
  // land on the same screen. An unknown path falls back to the overview rather
  // than rendering nothing — views.js decides what a view IS, and it already
  // answers null for anything it does not know.
  const booted = parseRoute(window.location.pathname, window.location.search);
  const [entityId, setEntityId] = useState(booted.entityId);
  const [view, setView] = useState(booted.view ?? DEFAULT_VIEW);
  // A payload for the view being opened, and the way one screen hands context
  // to another. Set by a drill-down (a trial balance row naming its account and
  // the period it was run for), read by the screen that lands. It also LIVES IN
  // THE QUERY STRING, which is what makes a drilled-down ledger something you
  // can link to, refresh, and go back from. Every plain navigation clears it,
  // which is why `go` and not `setView` is what the rail and every other caller
  // is given.
  const [viewArg, setViewArg] = useState(booted.arg);
  // Was the route on screen READ OFF THE ADDRESS BAR — a deep link, a refresh,
  // a back button — rather than chosen by a click? Only a route that arrived
  // that way gets corrected below; one you clicked your way to is already known
  // to be reachable, and one that stopped being reachable under you keeps
  // body()'s explanation instead of silently moving you somewhere else.
  const [fromBar, setFromBar] = useState(true);
  const [booting, setBooting] = useState(true);

  const load = useCallback(async () => {
    if (!getToken()) { setSession(null); setBooting(false); return; }
    try {
      const me = await api.get('/api/auth/me');
      setSession(me);
      // A company named by the URL is honoured only if it is one of this user's.
      // Anything else — a stale link, somebody else's company — falls back to
      // the first, because whatever this holds is what `go` writes into every
      // URL it pushes, and a dead id would travel from screen to screen.
      setEntityId((cur) => (me.entities.some((e) => e.id === cur)
        ? cur : me.entities[0]?.id ?? null));
    } catch {
      clearToken(); setSession(null);
    } finally { setBooting(false); }
  }, []);

  // The company on screen, and the capabilities that decide which screens exist
  // for it. Derived ABOVE the early returns because the route-correcting effect
  // below has to reach the same answer body() does — one definition, so the
  // address bar and the page can never disagree about what this role can see.
  const entity = session?.entities?.find((e) => e.id === entityId) ?? session?.entities?.[0];
  const caps = entity?.capabilities ?? [];

  /**
   * Navigate. THE URL IS THE NAVIGATION, not a decoration applied after it —
   * every call pushes a history entry, which is the whole of what makes the
   * back button work. `replace` is for a correction that should not become a
   * step you can go back to: landing on the overview because the URL named a
   * screen this role cannot reach is not a place the user chose to be.
   *
   * `company` is the entity the URL should name. It defaults to `entityId`,
   * which is right everywhere except the three handlers that call setEntityId
   * in the same breath — the client switcher, a newly created client, and
   * signing out. React has not re-rendered by then, so the closure still holds
   * the OLD id and the URL would name the company you just left; those three
   * pass the id they are moving to.
   */
  const go = (next, arg = null, { replace = false, company = entityId } = {}) => {
    setView(next); setViewArg(arg); setFromBar(false);
    const url = routeUrl({ view: next, entityId: company, arg });
    if (replace) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);
  };

  useEffect(() => { load(); }, [load]);

  // The back and forward buttons. The address bar is the source of truth on a
  // pop: read it and follow, rather than trying to keep a parallel stack.
  useEffect(() => {
    const onPop = () => {
      const r = parseRoute(window.location.pathname, window.location.search);
      setView(r.view ?? DEFAULT_VIEW);
      setViewArg(r.arg);
      setFromBar(true);
      if (r.entityId) setEntityId(r.entityId);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // The address bar has to agree with what is on screen, and it can drift two
  // ways. Both are corrected with REPLACE rather than push: a correction the
  // user did not ask for must not become a step the back button walks into a
  // second time.
  //
  // A token route is left completely alone — /accept-invite and /reset are not
  // views, and their screen is chosen before any of this runs.
  useEffect(() => {
    if (tokenRoute || !session || !entityId) return;

    // One: the URL named a screen this role cannot reach. Settle to the
    // overview — but only for a route that came off the bar, so that the guard
    // in body() still explains itself when a role changes under a live screen.
    if (fromBar) {
      setFromBar(false);
      const reachable = view === DEFAULT_VIEW || canView(view, caps)
        || ['account', 'new-entity', 'people', 'keys'].includes(view)
        // The Code tab belongs to the allowlist, not to a company or a role.
        || (view === 'code' && showCodeTab(session));
      if (!reachable) { go(DEFAULT_VIEW, null, { replace: true }); return; }
    }

    // Two: the URL named a company this user does not have, and `load` already
    // swapped it for a real one. The bar has to follow, or every later push
    // would carry the dead id from screen to screen.
    const url = routeUrl({ view, entityId, arg: viewArg });
    if (url !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState({}, '', url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenRoute, session, entityId, view, viewArg, fromBar]);

  // EVERY HOOK IS ABOVE THIS LINE, and has to be: React matches hooks by call
  // order, and the early returns below skip whatever follows them. An effect
  // written after one of them would change the order between a signed-out and a
  // signed-in render, and the app would throw on sign-in.

  const leaveTokenRoute = () => {
    window.history.replaceState({}, '', '/');
    setTokenRoute(null);
  };

  if (tokenRoute) {
    // A /set-password link with no usable token: missing, malformed, or a
    // purpose this app does not know. Not a blank page and not a crash — a
    // stated dead end, same voice as the rest of this screen.
    if (!tokenRoute.mode) {
      return (
        <div className="login"><div className="login__card">
          <h1 className="login__brand">Ledger</h1>
          <p className="login__tag">This link is not valid.</p>
          <div className="error">It may have already been used, or copied incompletely.</div>
          <button className="btn btn--quiet" style={{ marginTop: 12 }} onClick={leaveTokenRoute}>
            Back to sign in
          </button>
        </div></div>
      );
    }
    return (
      <SetPassword
        mode={tokenRoute.mode}
        token={tokenRoute.token}
        onDone={() => { leaveTokenRoute(); setBooting(true); load(); }}
      />
    );
  }

  if (booting) return <div className="login"><div className="login__card">Loading…</div></div>;
  if (forgot && !session) return <ForgotPassword onBack={() => setForgot(false)} />;
  if (!session) {
    return <Auth onSignedIn={() => { setBooting(true); load(); }} onForgot={() => setForgot(true)} />;
  }

  // Roles are per company, so moving between them can invalidate the current
  // view. Fall back to the overview rather than rendering a refusal.
  const changeEntity = (id) => {
    const next = session.entities.find((e) => e.id === id);
    const nextView = next && !canView(view, next.capabilities ?? []) ? DEFAULT_VIEW : view;
    // The company is half of what identifies a report, so switching client is a
    // navigation like any other and belongs in the history.
    //
    // The argument is dropped: an account id belongs to one company. Carrying
    // it across the switcher would filter the new company's ledger by an
    // account it does not have, and the screen remounts on entity change, so it
    // would be re-applied.
    go(nextView, null, { company: id });
    setEntityId(id);
  };

  const signOut = () => {
    clearToken(); setSession(null); setEntityId(null);
    go(DEFAULT_VIEW, null, { company: null });
  };

  const body = () => {
    // The nav hides what you cannot reach; this stops a view left over from a
    // previous company (or a hand-typed one) rendering a screen your role has
    // no business seeing. The API refuses either way — this is about not
    // showing a broken page instead of an explanation.
    if (!canView(view, caps) && !['dashboard', 'account', 'new-entity', 'people', 'keys', 'code'].includes(view)) {
      return (
        <div className="empty">
          <p>That is not available for this company.</p>
          <p><ViewLink className="btn btn--quiet" style={{ marginTop: 12 }}
                     view={DEFAULT_VIEW} entityId={entity?.id}
                     onNavigate={() => go(DEFAULT_VIEW)}>Back to the overview</ViewLink></p>
        </div>
      );
    }

    if (view === 'new-entity') {
      // `company: id` for the same reason the switcher passes it — setEntityId
      // has not landed, so `go`'s closure still names the previous client.
      return <NewEntity onCreated={(id) => {
        load().then(() => { setEntityId(id); go(DEFAULT_VIEW, null, { company: id }); });
      }} />;
    }
    // Practice-level screens: they are not about any one client company.
    if (view === 'people') {
      // A client administrator manages only their own company's people, so the
      // entity list they are offered is scoped the same way.
      const manageable = session.entities.filter(
        (e) => e.capabilities?.includes('people.practice') || e.capabilities?.includes('people.client'));
      return <People entities={manageable} currentUserId={session.user.id}
                     isPracticeUser={session.isPracticeUser} onChanged={load} />;
    }
    // A key spans clients, so this is a practice-level screen like People —
    // not scoped to whichever company happens to be selected.
    if (view === 'keys') return <ApiKeys entities={session.entities} />;
    if (view === 'account') return <Account user={session.user} />;
    // Not about any company: the queue is the deployment's. The server refuses
    // anyone off the allowlist (404); this keeps a hand-typed /code from
    // drawing a screen whose every call will fail.
    if (view === 'code') {
      if (!showCodeTab(session)) {
        return (
          <div className="empty">
            <p>That is not available.</p>
            <p><ViewLink className="btn btn--quiet" style={{ marginTop: 12 }}
                       view={DEFAULT_VIEW} entityId={entity?.id}
                       onNavigate={() => go(DEFAULT_VIEW)}>Back to the overview</ViewLink></p>
          </div>
        );
      }
      return <Code entityId={entityId} requestId={viewArg?.codeId ?? null} onView={go} />;
    }
    if (!entity) {
      return (
        <div className="empty">
          <p>No client companies yet.</p>
          <p><ViewLink className="btn" style={{ marginTop: 12 }} view="new-entity"
                     onNavigate={() => go('new-entity')}>Add your first client</ViewLink></p>
        </div>
      );
    }
    switch (view) {
      case 'trial-balance':
        return <TrialBalance entity={entity}
                             onOpenAccount={canView('general-ledger', caps)
                               ? (arg) => go('general-ledger', arg) : null} />;
      case 'income-statement': return <IncomeStatement entity={entity} />;
      case 'balance-sheet':    return <BalanceSheet entity={entity} />;
      // Given the SAME drill-down channel as the trial balance above, and it
      // needs no capability test: the contra column drills from the general
      // ledger to the general ledger, so anyone reading this screen may read
      // where it points. The arriving account is part of the remount key
      // below, which is what makes a drill from one account to another
      // actually change what is on screen.
      case 'general-ledger':   return <GeneralLedger entity={entity} arriving={viewArg}
                                                     onOpenAccount={(arg) => go('general-ledger', arg)} />;
      // The cash book LISTING, which is a report and lives with the reports.
      // The cash book SCREEN is `cash-book` below and is capture: one is read
      // after the fact, the other is typed into, and they are gated on
      // different capabilities (reports.ledger against cashbook.capture).
      case 'cash-book-listing': return <CashBookListing entity={entity} />;
      // The reconciliation STATEMENT, which is a report. The reconciliation
      // SCREEN is `bank-reconciliation` below and is capture: one prints what
      // the decisions add up to, the other is where they are made, and they are
      // gated on different capabilities (reports.ledger against
      // reconcile.import).
      case 'reconciliation-statement': return <ReconciliationStatement entity={entity} />;
      case 'vat':              return <VatReport entity={entity} />;
      case 'debtors':          return <AgeAnalysis entity={entity} type="customer" />;
      case 'creditors':        return <AgeAnalysis entity={entity} type="supplier" />;
      case 'journal':          return <JournalEntry entity={entity} />;
      case 'invoice':          return <Invoice entity={entity} />;
      case 'payment':          return <Payment entity={entity} />;
      // The URL is `/cash-book`, and it needs no route of its own: views.js
      // already declares the slug, so parseRoute resolves it, routeUrl pushes
      // it and the back button works. Which BATCH is open stays in the screen's
      // own state, as the stock take's sheet does — routing.js carries an
      // account and a date range, and widening its argument to a fourth kind of
      // id is a change to a shared table this screen does not need.
      case 'cash-book':        return <CashBook entity={entity} />;
      // `/bank-reconciliation`, and it needs no route of its own for the reason
      // the cash book above needs none: views.js declares the slug, so
      // parseRoute resolves it, routeUrl pushes it and the back button works.
      // WHICH STATEMENT is on screen stays in the screen's own state, as the
      // open batch does — routing.js carries an account and a date range, and
      // widening its argument to a fourth kind of id is a change to a shared
      // table this screen does not need.
      case 'bank-reconciliation': return <BankReconciliation entity={entity} />;
      case 'contacts':         return <Contacts entity={entity} />;
      case 'documents':        return <Documents entity={entity} />;
      case 'slips':            return <Slips entity={entity} onView={go} />;
      case 'slip-review':      return <SlipReview entity={entity} />;
      case 'allocation-rules': return <AllocationRules entity={entity} />;
      case 'description-rules': return <DescriptionRules entity={entity} />;
      case 'reference-prefixes': return <ReferencePrefixes entity={entity} />;
      case 'items':            return <Items entity={entity} />;
      case 'stock-on-hand':    return <StockOnHand entity={entity} />;
      case 'stock-take':       return <StockTake entity={entity} />;
      case 'stock-adjustments': return <StockAdjustments entity={entity} />;
      case 'stock-locations':  return <StockLocations entity={entity} />;
      case 'settings':         return <EntitySettings entity={entity} onSaved={load} />;
      case 'chart':            return <ChartOfAccounts entity={entity} />;
      case 'opening':          return <OpeningBalances entity={entity} />;
      case 'periods':          return <Periods entity={entity} />;
      default:                 return <Overview entity={entity} onView={go} />;
    }
  };

  return (
    <Shell user={session.user} tenant={session.tenant} entities={session.entities}
           entityId={entity?.id} onEntityChange={changeEntity}
           view={view} onView={go} onSignOut={signOut}
           isPracticeUser={session.isPracticeUser} canUseCode={showCodeTab(session)}>
      {/* Remount every view when the client changes, so no report is ever
          shown with one client's numbers under another client's name.

          THE ARGUMENT IS PART OF THE KEY, and it has to be: a drill-down reads
          `viewArg` once at mount, so without it here, arriving at a screen you
          are ALREADY on changes nothing. That is the rail's "General ledger"
          pressed while looking at one account's ledger — the arg clears, the
          key does not, and the filter you were trying to drop stays on. Every
          other view has a null arg before and after, so nothing else remounts
          that did not remount before. */}
      <div key={`${entity?.id}-${view}-${viewArg?.accountId ?? ''}`}>{body()}</div>
    </Shell>
  );
}
